import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IReceiving } from '@app/models/receiving.interface';
import { first, map, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectCurrentItemState } from '@app/modules/organization/store/selectors/organization.selectors';
import { selectReceivingsState } from '@app/modules/receivings/store/selectors/receivings.selectors';
import { SUPPRESS_TOASTR } from '@app/interceptors/error/http-error.interceptor';

@Injectable({
  providedIn: 'root'
})

export class ReceivingsService {
  public endpoint: string = `${environment.apiUrl}/receivings`;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;

  constructor(
    private _http: HttpClient,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
  }

  getReceivings(sortType, direction, query?, currentPage?,pageSize?) {
    this.store$.select(selectReceivingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}?page=${currentPage}`, {
      params: {
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }

  getReceiving(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  getReceivingsPath(id: number, locationId:number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/receivings-path/${id}?locationId=${locationId}`)
  }

  onSave(receiving: IReceiving, quiet: boolean = false): Observable<any> {
    return this._http.post<any>(this.endpoint, receiving, {
      context: new HttpContext().set(SUPPRESS_TOASTR, quiet)
    });
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  updateReceiving(receiving) {
    return this._http.put<any>(`${this.endpoint}/${receiving.productMovementHistoryID}`, receiving)
  }

  onDelete(id: number, reason: string) {
    const body = { reason };
    return this._http.delete(`${this.endpoint}/${id}`, { body });
  }

  searchReceivings(sort, direction, query: string): Observable<any> {
    this.store$.select(selectReceivingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  importReceivings(receivings: {}[]) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import`, {
      receivings
    }, { headers });
  }

  confirmImport(receivings: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirm`, {
      receivings
    });
  }

  confirmImportSkusBarcodes(skusBarcodes: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirmSkusBarcodes`, {
      skusBarcodes
    });
  }


  onSortReceivings(type: string, category?) {
    return this._http.get<any>(`${this.endpoint}/sort/${type}`, {
      params: {
        category: category
      }
    }).pipe(first()).subscribe((data) => {
      this.totalData$.next(data.total);
    });
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  batchDelete(selected, reason): Observable<any> {
    return this._http.post(`${this.endpoint}/batchDelete`, { ids: selected, reason:reason });
  }

  onSetIsAllowedToLeave(res) {
    return this.isAllowedToLeaveData$.next(res);
  }

  onGenerateExcel(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/receivings/export`);
  }

  onSort(arr, direction) {
    let type = 'name';

    if (arr.length <= 1) {
      return arr;
    }

    var pivot = arr[0];

    var left = [];
    var right = [];

    for (var i = 1; i < arr.length; i++) {
      if (direction == 'desc') {
        arr[i][type] < pivot[type] ? right.push(arr[i]) : left.push(arr[i]);
      } else {
        arr[i][type] < pivot[type] ? left.push(arr[i]) : right.push(arr[i]);
      }

    }

    return this.onSort(left, direction).concat(pivot, this.onSort(right, direction));
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

}

